import { tokenStore, API_BASE_URL } from './account/api.ts'
import type { ReleaseTarget } from './releases.ts'

type Environment = 'production' | 'test' | 'development' | 'local'
type Event = { schema_version: 1; event_id: string; event_type: 'website_visit' | 'download_requested' | 'identity_linked';
  user_id?: string; environment: Environment; source: 'website'; occurred_at: string; client_version: string; data: Record<string, string> }
type Queued = { visitor: string; payload: string; owner: string | null; state: 'pending' | 'rejected'; attempts: number; next: number }
const prefix = 'sciland.telemetry.v1.'
let activeUser: string | null = null
let identityReady = false
let verifiedToken: string | null = null
let verifiedTokenUser: string | null = null
let flushing = false
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/
const base = () => API_BASE_URL
const buildEnv = (import.meta as { env?: Record<string, string | undefined> }).env ?? {}
export function telemetryEnvironment(): Environment | null {
  const value = buildEnv.VITE_TELEMETRY_ENV as string | undefined
  return ['production', 'test', 'development', 'local'].includes(value ?? '') ? value as Environment : null
}
function scopeKey() { return `${prefix}${telemetryEnvironment()}.` }
function visitor(): string {
  // Identity/visitor state follows the tab's authenticated session. Different
  // accounts in different tabs must never share a mutable visitor binding.
  if (!identityReady && sessionStorage.getItem(`${scopeKey()}last_user`)) {
    sessionStorage.removeItem(`${scopeKey()}visitor`); sessionStorage.removeItem(`${scopeKey()}link_event`)
    sessionStorage.removeItem(`${scopeKey()}last_user`); sessionStorage.removeItem(`${scopeKey()}campaign`)
  }
  const key = `${scopeKey()}visitor`, existing = sessionStorage.getItem(key)
  if (existing && /^[0-9a-f]{64}$/.test(existing)) return existing
  const value = Array.from(crypto.getRandomValues(new Uint8Array(32)), b => b.toString(16).padStart(2, '0')).join('')
  sessionStorage.setItem(key, value); return value
}
function failure() { try { localStorage.setItem(`${scopeKey()}capture_failures`, String(Number(localStorage.getItem(`${scopeKey()}capture_failures`) ?? 0) + 1)) } catch { /* storage blocked */ } }
function enqueue(kind: Event['event_type'], data: Event['data'], id = crypto.randomUUID(), owner: string | null = null): boolean {
  const environment = telemetryEnvironment(); if (!environment) return false
  try {
    const key = `${scopeKey()}event.${id}`
    if (localStorage.getItem(key)) return true
    const event: Event = { schema_version: 1, event_id: id, event_type: kind, ...(owner ? { user_id: owner } : {}),
      environment, source: 'website', occurred_at: new Date().toISOString(), client_version: 'website-1', data }
    localStorage.setItem(key, JSON.stringify({ visitor: visitor(), payload: JSON.stringify(event), owner, state: 'pending', attempts: 0, next: 0 } satisfies Queued))
    void flushTelemetry()
    return true
  } catch { failure(); return false }
}
function attribution(): Pick<Event, 'data'>['data'] {
  const params = new URLSearchParams(location.search)
  const campaign = params.get('campaign_id') ?? params.get('utm_campaign')
  // Only a registered opaque campaign ID is retained. Query strings and referrer URLs are never stored.
  if (campaign && uuid.test(campaign)) {
    sessionStorage.setItem(`${scopeKey()}campaign`, campaign)
    return { channel: 'campaign', campaign_id: campaign }
  }
  const prior = sessionStorage.getItem(`${scopeKey()}campaign`)
  if (prior && uuid.test(prior)) return { channel: 'campaign', campaign_id: prior }
  if (params.get('utm_source') || params.get('utm_medium')) return { channel: 'campaign' }
  let channel = 'direct'
  if (document.referrer) {
    try { const host = new URL(document.referrer).hostname.toLowerCase()
      if (host === location.hostname) channel = 'direct'
      else if (/(^|\.)(google\.[a-z.]+|bing\.com|baidu\.com)$/.test(host)) channel = 'search'
      else if (/(^|\.)(weibo\.com|xiaohongshu\.com|x\.com|t\.co|linkedin\.com)$/.test(host)) channel = 'social'
      else channel = 'referral'
    } catch { channel = 'unknown' }
  }
  return { channel }
}
const pages = new Set(['features', 'guides', 'cases', 'scenarios', 'enterprise', 'about', 'download', 'login', 'redeem', 'account', 'terms', 'privacy'])
let lastNavigation = ''
export function recordPageVisit(path: string, navigationKey: string): void {
  if (!telemetryEnvironment() || navigationKey === lastNavigation) return
  lastNavigation = navigationKey
  try { const page = path === '/' ? 'home' : path.replace(/^\//, '')
    enqueue('website_visit', { visit_id: crypto.randomUUID(), page: pages.has(page) || page === 'home' ? page : 'unknown', ...attribution() })
  } catch { failure() }
}
export function recordDownloadRequest(target: ReleaseTarget, releaseVersion: string): void {
  if (!telemetryEnvironment()) return
  try { const source = attribution()
    enqueue('download_requested', { download_id: crypto.randomUUID(), target: target === 'mac-arm64' ? 'macos_arm64' : 'windows_x64', release_version: releaseVersion,
      ...(source.campaign_id ? { campaign_id: source.campaign_id } : {}) })
  } catch { failure() }
}
export function setTelemetryUser(userId: string | null): void {
  if (!telemetryEnvironment()) return
  try {
    const previous = sessionStorage.getItem(`${scopeKey()}last_user`)
    if (previous && previous !== userId) {
      sessionStorage.removeItem(`${scopeKey()}visitor`)
      sessionStorage.removeItem(`${scopeKey()}link_event`)
      sessionStorage.removeItem(`${scopeKey()}campaign`)
    }
    identityReady = true
    activeUser = userId
    verifiedToken = userId ? tokenStore.getAccessToken() : null
    verifiedTokenUser = userId
    if (!userId) { sessionStorage.removeItem(`${scopeKey()}last_user`); return }
    if (!uuid.test(userId)) return
    sessionStorage.setItem(`${scopeKey()}last_user`, userId)
    const marker = sessionStorage.getItem(`${scopeKey()}link_event`)
    if (!marker) {
      const id = crypto.randomUUID()
      if (enqueue('identity_linked', { method: 'website_login' }, id, userId)) sessionStorage.setItem(`${scopeKey()}link_event`, id)
    }
    void flushTelemetry()
  } catch { failure() }
}
async function sha256(raw: string): Promise<string> {
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw))
  return Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2, '0')).join('')
}
export async function flushTelemetry(): Promise<void> {
  if (flushing || !telemetryEnvironment()) return
  flushing = true
  try {
    const currentToken = tokenStore.getAccessToken()
    if (activeUser && currentToken && currentToken !== verifiedToken) {
      // Refresh may rotate a token before the UI updates. Verify its owner once;
      // never send A's pending events using a newly installed B credential.
      try {
        const response = await fetch(`${base()}/v1/user/me`, { headers: { Authorization: `Bearer ${currentToken}` }, signal: AbortSignal.timeout(10000) })
        if (response.ok) {
          const user = await response.json() as { id?: string }
          if (user.id && uuid.test(user.id) && tokenStore.getAccessToken() === currentToken) { verifiedToken = currentToken; verifiedTokenUser = user.id }
        }
      } catch { /* Anonymous observations can still flush. */ }
    }
    const keys = Object.keys(localStorage).filter(key => key.startsWith(`${scopeKey()}event.`))
    let processed = 0
    for (const key of keys) {
      const raw = localStorage.getItem(key); if (!raw) continue
      let queued: Queued
      try { queued = JSON.parse(raw) as Queued } catch { failure(); continue }
      if (queued.state !== 'pending' || queued.next > Date.now() || queued.owner && queued.owner !== activeUser) continue
      const token = queued.owner ? tokenStore.getAccessToken() : null
      if (queued.owner && (!token || token !== verifiedToken || queued.owner !== verifiedTokenUser)) continue
      if (++processed > 100) break
      queued.attempts++; queued.next = Date.now() + Math.min(300000, 1000 * 2 ** Math.min(queued.attempts, 8))
      localStorage.setItem(key, JSON.stringify(queued))
      try {
        const res = await fetch(`${base()}/v1/telemetry/${queued.owner ? '' : 'anonymous/'}batch`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: `{"visitor_token":"${queued.visitor}","events":[${queued.payload}]}`, keepalive: true, signal: AbortSignal.timeout(10000),
        })
        if (!res.ok) continue
        const result = await res.json() as { results?: Array<{ event_id: string; status: string; retryable: boolean; payload_sha256?: string; received_at?: string }> }
        const event = JSON.parse(queued.payload) as Event, ack = result.results?.length === 1 ? result.results[0] : undefined
        if (!ack || ack.event_id !== event.event_id) continue
        if (['accepted', 'duplicate'].includes(ack.status) && ack.payload_sha256 === await sha256(queued.payload) && ack.received_at && Number.isFinite(Date.parse(ack.received_at))) localStorage.removeItem(key)
        else if (ack.status === 'rejected' && ack.retryable === false) { queued.state = 'rejected'; localStorage.setItem(key, JSON.stringify(queued)) }
      } catch { /* keep the original identity/time for retry */ }
    }
  } catch { failure() } finally { flushing = false }
}
export function startTelemetry(): () => void {
  const online = () => { void flushTelemetry() }
  window.addEventListener('online', online)
  const timer = window.setInterval(online, 15000)
  online()
  return () => { window.removeEventListener('online', online); window.clearInterval(timer) }
}
